import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Shield, User } from 'lucide-react';
import { type UserProfile } from '@/types/team';
import { type Department } from '@/types/org';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { ThemeToggle } from '@/components/ui/ThemeToggle';

interface UserMenuProps {
    profile: UserProfile | null;
    departments: Department[];
    profileLink: string;
    signOut: () => Promise<void>;
}

/**
 * WEB-111: the user block that sat at the foot of the left rail (name, role, department, Profile, theme, Sign Out,
 * Privacy Policy), as a menu behind the avatar at the right of the top bar. Escape or a click outside closes it.
 */
export const UserMenu: React.FC<UserMenuProps> = ({ profile, departments, profileLink, signOut }) => {
    const [open, setOpen] = useState(false);
    const root = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        const onDown = (e: MouseEvent) => { if (root.current && !root.current.contains(e.target as Node)) setOpen(false); };
        window.addEventListener('keydown', onKey);
        window.addEventListener('mousedown', onDown);
        return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mousedown', onDown); };
    }, [open]);
    const department = profile?.departmentId ? departments.find((d) => d.id === profile.departmentId)?.name : undefined;
    const item = 'w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-text-primary hover:bg-surface-hover no-underline cursor-pointer bg-transparent border-none text-left';
    return (
        <div ref={root} className="relative shrink-0">
            <button
                type="button"
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="w-9 h-9 rounded-[10px] bg-bg-tertiary flex items-center justify-center overflow-hidden border border-divider-subtle cursor-pointer"
            >
                <UserAvatar user={profile} />
            </button>
            {open && (
                <div role="menu" aria-label="Account" className="absolute right-0 top-[calc(100%+8px)] w-64 z-50 rounded-2xl border border-divider-subtle bg-bg-primary/95 backdrop-blur-xl p-2 shadow-2xl">
                    <div className="px-3 py-2">
                        <div className="text-sm font-bold text-text-primary truncate">{profile?.displayName || 'User'}</div>
                        <div className="text-xs text-text-secondary capitalize">{profile?.role.replace('_', ' ').toLowerCase()}</div>
                        {department && <div className="text-xs text-accent-electric font-medium mt-0.5">{department}</div>}
                    </div>
                    <div className="h-px bg-surface-active my-1" />
                    <Link role="menuitem" to={profileLink} className={item} onClick={() => setOpen(false)}><User size={16} /> Profile</Link>
                    <div className="flex items-center justify-between px-3 py-1.5 text-sm text-text-secondary">Theme <ThemeToggle /></div>
                    <Link role="menuitem" to="/privacy" className={item} onClick={() => setOpen(false)}><Shield size={16} /> Privacy Policy</Link>
                    <button role="menuitem" type="button" onClick={() => { setOpen(false); void signOut(); }} className={`${item} text-[#ff4444]`}><LogOut size={16} /> Sign Out</button>
                </div>
            )}
        </div>
    );
};
